// Deployment-mode flag for the UI.
//
// Single fetch at app bootstrap from GET /api/tenant/deployment/config. The
// returned mode ("single_tenant" | "saas") tells the UI whether to hide
// multi-tenant ceremony (tenant selector on login, /t/<tenant>/ URL prefix,
// tenant chip in profile, install-template marketplace, BYOD wizard).
//
// Synchronous accessors below assume bootstrap has finished — call
// loadDeploymentConfig() once before rendering routed UI.

import { logger } from '../utils/logger';

let cached = null;

// Build-time edition (webpack DefinePlugin literal). In a STANDARD build the Pro
// discovery components are compiled to () => null (see proComponents.js), so the
// runtime edition/features must NEVER claim Pro in a Standard build — otherwise
// call sites render the null stub (blank Cost Saving / Productivity tabs)
// instead of the locked-Pro teaser. This was the fetch-failure fallback bug:
// the fallback defaulted to Pro-everything regardless of the build.
const IS_PRO_BUILD = process.env.REACT_APP_EDITION !== 'standard';

// Mirror of tenant-api core/edition.py `_PRO_ONLY_FEATURES` — the capabilities a
// Standard edition drops. Keep in sync with the backend.
const PRO_ONLY_FEATURES = ['productivity', 'forecasting', 'estimation', 'savings'];

const DEFAULT_FEATURES = {
  usage: true,
  adoption: true,
  billing: true,
  budget: true,
  productivity: true,
  forecasting: true,
  estimation: true,
  savings: true,
};

// Standard drops the Pro-only capabilities.
const STANDARD_FEATURES = { ...DEFAULT_FEATURES };
PRO_ONLY_FEATURES.forEach((k) => { STANDARD_FEATURES[k] = false; });

// The fail-safe fallback matches the BUILD: a Standard build falls back to the
// Standard edition (its Pro surfaces are not in the bundle); a Pro build to Pro.
const DEFAULT_CONFIG = {
  mode: 'single_tenant',
  single_tenant: true,
  default_tenant_id: null,
  default_workspace_id: null,
  edition: IS_PRO_BUILD ? 'pro' : 'standard',
  is_pro: IS_PRO_BUILD,
  features: IS_PRO_BUILD ? DEFAULT_FEATURES : STANDARD_FEATURES,
  allowed_sources: null,
  // Fail CLOSED, unlike `features` below: the Dagster UI is an operator
  // surface that most deployments switch off, so a failed config fetch must
  // hide its links rather than offer buttons that 404.
  dagit: { enabled: false },
};

export async function loadDeploymentConfig() {
  try {
    const res = await fetch('/api/tenant/deployment/config', {
      credentials: 'include',
    });
    if (!res.ok) {throw new Error(`HTTP ${res.status}`);}
    cached = await res.json();
  } catch (err) {
    // Fail closed to single-tenant — this is the safer default for a
    // client-hosted deployment and matches the backend default.
    logger.warn('[deploymentConfig] fetch failed, defaulting to single_tenant:', err);
    cached = DEFAULT_CONFIG;
  }
  return cached;
}

export function getDeploymentConfig() {
  return cached || DEFAULT_CONFIG;
}

export function isSingleTenant() {
  return getDeploymentConfig().single_tenant === true;
}

export function isSaas() {
  return !isSingleTenant();
}

// ── Edition (Standard vs Pro) ───────────────────────────────────────
// Defaults to Pro when the backend doesn't report an edition (older
// backend or failed fetch) — Standard only ever removes surfaces.

export function getEdition() {
  // A Standard BUILD can never be Pro at runtime — the Pro code isn't shipped,
  // so the build wins over whatever the config fetch (or its fallback) reports.
  if (!IS_PRO_BUILD) {return 'standard';}
  return getDeploymentConfig().edition === 'standard' ? 'standard' : 'pro';
}

export function isPro() {
  return getEdition() !== 'standard';
}

export function isStandard() {
  return getEdition() === 'standard';
}

// ── Dagster (Dagit) UI ──────────────────────────────────────────────
// Is the Dagster UI reachable on this deployment? Backed by the backend's
// DAGIT_PROXY_ENABLED; most deployments leave it off, and then the reverse
// proxy isn't even mounted, so every "open in Dagster" control must be
// hidden instead of linking into a 404.
//
// Deliberately NOT routed through hasFeature(): that helper treats an unknown
// key as enabled (so a new capability is never hidden by an older config),
// which is exactly the wrong default here.
export function isDagitEnabled() {
  const cfg = getDeploymentConfig();
  return cfg.dagit ? cfg.dagit.enabled === true : false;
}

// Capability gate. Unknown features default to enabled so a new feature is
// never accidentally hidden before this map learns about it.
export function hasFeature(key) {
  // Never report a Pro-only capability as available in a Standard build — its
  // component is compiled out, so the call site must show the locked-Pro teaser
  // (via makeProTab) rather than render an empty () => null stub.
  if (!IS_PRO_BUILD && PRO_ONLY_FEATURES.includes(key)) {return false;}
  const features = getDeploymentConfig().features || DEFAULT_FEATURES;
  return features[key] !== false;
}
